import { useLocation, useNavigate } from "react-router-dom"

import { APP_ROUTES } from "@/constants/routes"
import {
  isWebMaintenanceEnabled,
  useStaticConfig,
} from "@/hooks/use-static-config"
import { useEffect } from "react"

interface StoredMaintenanceRoute {
  pathname: string
  search: string
  hash: string
}

const LAST_NON_MAINTENANCE_ROUTE_KEY = "avacus:last-non-maintenance-route"

export function isMaintenancePage(pathname: string) {
  return (
    pathname === APP_ROUTES.maintenance ||
    pathname.startsWith(`${APP_ROUTES.maintenance}/`)
  )
}

function isValidMaintenanceRedirectRoute(pathname: string) {
  if (isMaintenancePage(pathname)) {
    return false
  }

  const sectionRoots = [
    APP_ROUTES.auth,
    APP_ROUTES.gasAccount,
    APP_ROUTES.policies,
    APP_ROUTES.ads,
    APP_ROUTES.analytics,
    APP_ROUTES.settings,
  ] as const

  if (pathname === APP_ROUTES.home) {
    return true
  }

  return sectionRoots.some(
    (root) => pathname === root || pathname.startsWith(`${root}/`)
  )
}

function buildRoutePath(route: StoredMaintenanceRoute) {
  return `${route.pathname}${route.search}${route.hash}`
}

function getLastNonMaintenanceRoute() {
  try {
    const rawRoute = sessionStorage.getItem(LAST_NON_MAINTENANCE_ROUTE_KEY)

    if (!rawRoute) {
      return null
    }

    const route = JSON.parse(rawRoute) as Partial<StoredMaintenanceRoute>

    if (!route.pathname || !isValidMaintenanceRedirectRoute(route.pathname)) {
      return null
    }

    return {
      pathname: route.pathname,
      search: route.search ?? "",
      hash: route.hash ?? "",
    }
  } catch {
    return null
  }
}

function saveLastNonMaintenanceRoute(route: StoredMaintenanceRoute) {
  if (!isValidMaintenanceRedirectRoute(route.pathname)) {
    return
  }

  try {
    sessionStorage.setItem(
      LAST_NON_MAINTENANCE_ROUTE_KEY,
      JSON.stringify(route)
    )
  } catch {
    // Ignore storage write failures and continue navigation flow.
  }
}

function clearLastNonMaintenanceRoute() {
  try {
    sessionStorage.removeItem(LAST_NON_MAINTENANCE_ROUTE_KEY)
  } catch {
    // Ignore storage clear failures.
  }
}

function getMaintenanceExitRoute() {
  const lastRoute = getLastNonMaintenanceRoute()

  if (!lastRoute) {
    return APP_ROUTES.home
  }

  return buildRoutePath(lastRoute)
}

export function useResolvedMaintenanceMode() {
  const staticConfigQuery = useStaticConfig()
  const fallbackMaintenanceMode =
    import.meta.env.VITE_MAINTENANCE_MODE === "true"
  const isResolvingMaintenanceMode = staticConfigQuery.isPending
  const isMaintenanceMode = staticConfigQuery.isSuccess
    ? isWebMaintenanceEnabled(staticConfigQuery.data)
    : fallbackMaintenanceMode

  return {
    isMaintenanceMode,
    isResolvingMaintenanceMode,
    staticConfigQuery,
  }
}

export function useMaintenanceMode() {
  const { isMaintenanceMode, isResolvingMaintenanceMode } =
    useResolvedMaintenanceMode()
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  useEffect(() => {
    if (isResolvingMaintenanceMode) {
      return
    }

    const isCurrentPageMaintenancePage = isMaintenancePage(pathname)

    if (isCurrentPageMaintenancePage) {
      if (!isMaintenanceMode) {
        const exitRoute = getMaintenanceExitRoute()

        clearLastNonMaintenanceRoute()
        navigate(exitRoute, { replace: true })
      }

      return
    }

    if (isMaintenanceMode && isValidMaintenanceRedirectRoute(pathname)) {
      saveLastNonMaintenanceRoute({
        pathname,
        search: location.search,
        hash: location.hash,
      })
      navigate(APP_ROUTES.maintenance, { replace: true })
    }
  }, [
    location.hash,
    location.search,
    pathname,
    isMaintenanceMode,
    isResolvingMaintenanceMode,
    navigate,
  ])

  return { isMaintenanceMode, isResolvingMaintenanceMode }
}
